import {
	resolveThemeColors,
	statCardTokens,
	statusTokens,
	themeTokens,
	type StatCardVariant,
	type ThemeColorToken,
	type ThemeMode,
} from "@config/theme/tokens";

export const CSS_VAR = {
	color: {
		primary: "--tvet-primary",
		primaryForeground: "--tvet-primary-foreground",
		primaryHover: "--tvet-primary-hover",
		secondary: "--tvet-secondary",
		secondaryForeground: "--tvet-secondary-foreground",
		background: "--tvet-background",
		foreground: "--tvet-foreground",
		surface: "--tvet-surface",
		surfaceForeground: "--tvet-surface-foreground",
		surfaceMuted: "--tvet-surface-muted",
		muted: "--tvet-muted",
		mutedForeground: "--tvet-muted-foreground",
		border: "--tvet-border",
		borderStrong: "--tvet-border-strong",
		danger: "--tvet-danger",
		dangerForeground: "--tvet-danger-foreground",
		success: "--tvet-success",
		successForeground: "--tvet-success-foreground",
		warning: "--tvet-warning",
		warningForeground: "--tvet-warning-foreground",
		info: "--tvet-info",
		infoForeground: "--tvet-info-foreground",
		focusRing: "--tvet-focus-ring",
		overlay: "--tvet-overlay",
		primarySoft: "--tvet-primary-soft",
		primarySoftHover: "--tvet-primary-soft-hover",
		primarySoftBorder: "--tvet-primary-soft-border",
		primarySoftText: "--tvet-primary-soft-text",
	},
	status: {
		warningSurface: "--tvet-status-warning-surface",
		warningText: "--tvet-status-warning-text",
		warningAccent: "--tvet-status-warning-accent",
	},
	shell: {
		background: "--tvet-shell-bg",
		backgroundElevated: "--tvet-shell-bg-elevated",
		foreground: "--tvet-shell-fg",
		headerBackground: "--tvet-header-bg",
		headerBorder: "--tvet-header-border",
	},
	auth: {
		knowledge: "--tvet-auth-knowledge",
		skills: "--tvet-auth-skills",
		behavior: "--tvet-auth-behavior",
		heading: "--tvet-auth-heading",
	},
	font: {
		sans: "--tvet-font-sans",
		mono: "--tvet-font-mono",
	},
	radius: {
		sm: "--tvet-radius-sm",
		md: "--tvet-radius-md",
		lg: "--tvet-radius-lg",
		xl: "--tvet-radius-xl",
	},
	shadow: {
		sm: "--tvet-shadow-sm",
		card: "--tvet-shadow-card",
		md: "--tvet-shadow-md",
		lg: "--tvet-shadow-lg",
	},
	layout: {
		sidebar: "--tvet-sidebar-width",
		header: "--tvet-header-height",
		footer: "--tvet-footer-height",
	},
	component: {
		btnHeightSm: "--tvet-btn-height-sm",
		btnHeightMd: "--tvet-btn-height-md",
		btnHeightLg: "--tvet-btn-height-lg",
		inputHeight: "--tvet-input-height",
		inputRadius: "--tvet-input-radius",
	},
	statCard: (variant: StatCardVariant) => ({
		accent: `--tvet-stat-${variant}-accent`,
		surface: `--tvet-stat-${variant}-surface`,
	}),
} as const;

function getStatCardCssVariables(): Record<string, string> {
	const entries: [string, string][] = [];

	for (const variant of Object.keys(statCardTokens) as StatCardVariant[]) {
		const colors = statCardTokens[variant];
		const vars = CSS_VAR.statCard(variant);
		entries.push([vars.accent, colors.accent], [vars.surface, colors.surface]);
	}

	return Object.fromEntries(entries);
}

const colorTokenToCssVar: Record<ThemeColorToken, string> = {
	primary: CSS_VAR.color.primary,
	primaryForeground: CSS_VAR.color.primaryForeground,
	primaryHover: CSS_VAR.color.primaryHover,
	secondary: CSS_VAR.color.secondary,
	secondaryForeground: CSS_VAR.color.secondaryForeground,
	background: CSS_VAR.color.background,
	foreground: CSS_VAR.color.foreground,
	surface: CSS_VAR.color.surface,
	surfaceForeground: CSS_VAR.color.surfaceForeground,
	surfaceMuted: CSS_VAR.color.surfaceMuted,
	muted: CSS_VAR.color.muted,
	mutedForeground: CSS_VAR.color.mutedForeground,
	border: CSS_VAR.color.border,
	borderStrong: CSS_VAR.color.borderStrong,
	danger: CSS_VAR.color.danger,
	dangerForeground: CSS_VAR.color.dangerForeground,
	success: CSS_VAR.color.success,
	successForeground: CSS_VAR.color.successForeground,
	warning: CSS_VAR.color.warning,
	warningForeground: CSS_VAR.color.warningForeground,
	info: CSS_VAR.color.info,
	infoForeground: CSS_VAR.color.infoForeground,
	focusRing: CSS_VAR.color.focusRing,
	overlay: CSS_VAR.color.overlay,
	primarySoft: CSS_VAR.color.primarySoft,
	primarySoftHover: CSS_VAR.color.primarySoftHover,
	primarySoftBorder: CSS_VAR.color.primarySoftBorder,
	primarySoftText: CSS_VAR.color.primarySoftText,
};

function getStatusCssVariables(): Record<string, string> {
	return {
		[CSS_VAR.status.warningSurface]: statusTokens.warningSurface,
		[CSS_VAR.status.warningText]: statusTokens.warningText,
		[CSS_VAR.status.warningAccent]: statusTokens.warningAccent,
	};
}

export function getCssVariables(mode: ThemeMode = "light"): Record<string, string> {
	const colors = resolveThemeColors(mode);
	const { shared, shell, auth } = themeTokens;

	const colorVars = Object.fromEntries(
		(Object.keys(colorTokenToCssVar) as ThemeColorToken[]).map(key => [
			colorTokenToCssVar[key],
			colors[key],
		]),
	);

	return {
		...colorVars,
		[CSS_VAR.shell.background]: shell.background,
		[CSS_VAR.shell.backgroundElevated]: shell.backgroundElevated,
		[CSS_VAR.shell.foreground]: shell.foreground,
		[CSS_VAR.shell.headerBackground]: shell.headerBackground,
		[CSS_VAR.shell.headerBorder]: shell.headerBorder,
		[CSS_VAR.auth.knowledge]: auth.knowledge,
		[CSS_VAR.auth.skills]: auth.skills,
		[CSS_VAR.auth.behavior]: auth.behavior,
		[CSS_VAR.auth.heading]: auth.heading,
		[CSS_VAR.font.sans]: shared.typography.fontFamily.sans,
		[CSS_VAR.font.mono]: shared.typography.fontFamily.mono,
		[CSS_VAR.radius.sm]: shared.radius.sm,
		[CSS_VAR.radius.md]: shared.radius.md,
		[CSS_VAR.radius.lg]: shared.radius.lg,
		[CSS_VAR.radius.xl]: shared.radius.xl,
		[CSS_VAR.shadow.sm]: shared.shadow.sm,
		[CSS_VAR.shadow.card]: shared.shadow.card,
		[CSS_VAR.shadow.md]: shared.shadow.md,
		[CSS_VAR.shadow.lg]: shared.shadow.lg,
		[CSS_VAR.layout.sidebar]: shared.layout.sidebarWidth,
		[CSS_VAR.layout.header]: shared.layout.headerHeight,
		[CSS_VAR.layout.footer]: shared.layout.footerHeight,
		[CSS_VAR.component.btnHeightSm]: themeTokens.components.button.heightSm,
		[CSS_VAR.component.btnHeightMd]: themeTokens.components.button.heightMd,
		[CSS_VAR.component.btnHeightLg]: themeTokens.components.button.heightLg,
		[CSS_VAR.component.inputHeight]: themeTokens.components.input.height,
		[CSS_VAR.component.inputRadius]: themeTokens.components.input.radius,
		...getStatCardCssVariables(),
		...getStatusCssVariables(),
	};
}
